/**
 * Tenant management Zod schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.2 (Tenant endpoints) + ARCHITECTURE.md §3.3.
 */
import { z } from 'zod';

/* ─── Constants ────────────────────────────────────────────────── */

/** Plan-tier codes — must match seeded `plan_tiers.code`. */
export const PLAN_TIER_CODES = ['FREE', 'PRO', 'UNLIMITED'] as const;
export type PlanTierCode = (typeof PLAN_TIER_CODES)[number];

/** Length unit — currently MM-only; INCHES is a future addition. */
export const UNIT_CODES = ['MM'] as const;
export type UnitCode = (typeof UNIT_CODES)[number];

/* ─── Update tenant settings (PATCH /tenant/me) ────────────────── */

export const UpdateTenantSettingsSchema = z
  .object({
    /** Tenant display name. */
    name: z.string().trim().min(1, 'Name is required').max(200, 'Name too long').optional(),
    /** Saw kerf in mm (1–20). */
    kerfMm: z.number().int('Must be a whole number').min(1, 'Min 1mm').max(20, 'Max 20mm').optional(),
    /** Threshold below which a remainder is waste rather than a reusable offcut. */
    offcutThresholdMm: z
      .number()
      .int('Must be a whole number')
      .min(50, 'Min 50mm')
      .max(2000, 'Max 2000mm')
      .optional(),
    /** Default stock-bar length (overridable per project / per line item). */
    defaultStockBarMm: z
      .number()
      .int('Must be a whole number')
      .min(1000, 'Min 1000mm')
      .max(12000, 'Max 12000mm')
      .optional(),
    defaultUnitCode: z.enum(UNIT_CODES).optional(),
    /** Optional free-text remarks for the tenant row. */
    remarks: z.string().max(1000).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
  });

export type UpdateTenantSettingsInput = z.infer<typeof UpdateTenantSettingsSchema>;

/* ─── Tenant response DTOs ─────────────────────────────────────── */

export const TenantDtoSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  planTierCode: z.enum(PLAN_TIER_CODES),
  defaults: z.object({
    kerfMm: z.number().int(),
    offcutThresholdMm: z.number().int(),
    defaultStockBarMm: z.number().int(),
    unitCode: z.enum(UNIT_CODES),
  }),
  hasLogo: z.boolean(),
});

export type TenantDto = z.infer<typeof TenantDtoSchema>;

/** Per-metric usage row returned by GET /tenant/me. */
export const TenantUsageItemSchema = z.object({
  metric: z.string(),
  used: z.number().int().min(0),
  /** null = Unlimited. */
  limit: z.number().int().nullable(),
});

export const TenantUsageDtoSchema = z.object({
  planTierCode: z.enum(PLAN_TIER_CODES),
  items: z.array(TenantUsageItemSchema),
});

export type TenantUsageDto = z.infer<typeof TenantUsageDtoSchema>;
